import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
const plans = [{
  name: 'Starter',
  description: 'For solo artists and small teams getting organized.',
  price: 0,
  priceLabel: 'Free',
  priceSuffix: 'to start',
  features: ['1 artist or venue profile', '3 active campaigns', 'Basic venue/artist discovery', 'Deal tracking', 'Email outreach templates'],
  cta: 'Start free',
  highlighted: false
}, {
  name: 'Pro',
  description: 'For active artists, venues, and managers running real workflow volume.',
  price: 49,
  priceLabel: '$49',
  priceSuffix: '/mo',
  features: ['Unlimited campaigns', 'Route planner with economics', 'Full outreach sequences', 'Contracts & e-sign', 'Deposit tracking', 'Lineup management', 'AI assistant'],
  cta: 'Start free trial',
  highlighted: true
}, {
  name: 'Team',
  description: 'For agencies, managers, and venue teams who need seats and advanced ops.',
  price: 149,
  priceLabel: '$149',
  priceSuffix: '/mo',
  features: ['Everything in Pro', 'Up to 10 team seats', 'Multi-artist roster', 'Role-based permissions', 'Approval workflows', 'Advanced analytics', 'Priority support'],
  cta: 'Book a demo',
  highlighted: false
}];
// Animated Counter Component
const AnimatedNumber = ({
  value,
  isFree
}: {
  value: number;
  isFree: boolean;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (isInView && !isFree) {
      let start = 0;
      const end = value;
      const duration = 1500;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCurrent(end);
          clearInterval(timer);
        } else {
          setCurrent(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value, isFree]);
  return <span ref={ref} className="text-4xl font-bold" data-id="element-438">
      {isFree ? 'Free' : `$${current}`}
    </span>;
};
export function PricingTeaser() {
  return <section id="pricing" className="py-24 border-t border-border relative overflow-hidden" data-id="element-439">
      <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-440">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-441">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-442">
            Simple pricing for booking workflows that actually get used.
          </h2>
          <p className="text-textMuted text-lg max-w-2xl mx-auto" data-id="element-443">
            Upgrade when you need more campaigns, more outreach, deeper
            contracts and payments, or team workflows.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-444">
          {plans.map((plan, index) => {
          const isHighlighted = plan.highlighted;
          return <motion.div key={plan.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5
          }} className={`relative rounded-xl flex flex-col transition-all duration-300 ${isHighlighted ? 'glow-border-wrapper shadow-2xl scale-105 z-10' : 'bg-surface/50 backdrop-blur-sm border border-border hover:shadow-xl'}`} data-id="element-445">
                <div className={`${isHighlighted ? 'glow-border-content bg-[#14141A] p-8 flex flex-col' : 'p-8 flex flex-col h-full'}`} data-id="element-446">
                  {isHighlighted && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-background text-xs font-bold rounded-full shadow-[0_0_10px_rgba(245,158,11,0.5)] animate-pulse" data-id="element-447">
                      Most popular
                    </div>}

                  <h3 className="text-xl font-bold mb-2" data-id="element-448">{plan.name}</h3>
                  <p className="text-sm text-textMuted mb-6 h-10" data-id="element-449">
                    {plan.description}
                  </p>

                  <div className="flex items-baseline gap-1 mb-8" data-id="element-450">
                    <AnimatedNumber value={plan.price} isFree={plan.price === 0} data-id="element-451" />
                    <span className="text-textMuted text-sm" data-id="element-452">
                      {plan.priceSuffix}
                    </span>
                  </div>

                  <ul className="flex flex-col gap-3 mb-8 flex-1" data-id="element-453">
                    {plan.features.map(feature => <li key={feature} className="flex items-start gap-3 text-sm" data-id="element-454">
                        <CheckIcon className="w-4 h-4 text-primary shrink-0 mt-0.5" data-id="element-455" />
                        <span className="text-textMuted" data-id="element-456">{feature}</span>
                      </li>)}
                  </ul>

                  <button className={`w-full py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 group ${isHighlighted ? 'bg-primary text-background hover:bg-primaryHover shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.6)]' : 'border border-border text-textMain hover:bg-surfaceHover'}`} data-id="element-457">
                    {plan.cta}
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-458" />
                  </button>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}